import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, loading } = useAuth();

  const navigationItems = [
    {
      label: 'Find Vendors',
      path: '/homepage',
      icon: 'Search',
      description: 'Discover wedding vendors'
    },
    {
      label: 'Browse Categories',
      path: '/browse-categories-all-vendor-types',
      icon: 'Grid3X3',
      description: 'Explore by category'
    },
    {
      label: 'All Vendors',
      path: '/all-vendors',
      icon: 'Users',
      description: 'View all vendors'
    }
  ];

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSignIn = () => {
    closeMobileMenu();
    navigate('/login');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      closeMobileMenu();
      navigate('/');
    } catch (error) {
      console.log('Error signing out:', error);
    }
  };

  const handleDashboard = () => {
    closeMobileMenu();
    navigate('/user-dashboard');
  };

  const handleVendorRegistration = () => {
    closeMobileMenu();
    navigate('/vendor-registration');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border shadow-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/homepage"
            className="flex items-center space-x-3 transition-smooth hover:opacity-80"
            onClick={closeMobileMenu}
          >
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-primary-600 rounded-lg shadow-soft">
              <Icon name="Heart" size={24} color="#FFFFFF" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-bold text-xl text-primary tracking-tight">
                ShaadiNest
              </span>
              <span className="font-caption text-xs text-text-secondary -mt-1">
                Wedding Marketplace
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${isActivePath(item.path)
                    ? 'bg-primary-50 text-primary border border-primary-200' : 'text-text-secondary hover:text-primary hover:bg-surface-100'
                  }`}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActivePath(item.path) ? '#667eea' : 'currentColor'}
                />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Bell"
                  iconSize={18}
                  className="text-text-secondary hover:text-primary"
                  onClick={() => navigate('/notifications')}
                >
                  Notifications
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="User"
                  iconSize={18}
                  onClick={handleDashboard}
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="LogOut"
                  iconSize={18}
                  onClick={handleSignOut}
                  disabled={loading}
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="User"
                  iconSize={18}
                  onClick={handleSignIn}
                  disabled={loading}
                >
                  Sign In
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconName="UserPlus"
                  iconSize={18}
                  onClick={handleVendorRegistration}
                >
                  Join as Vendor
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-text-secondary hover:text-primary hover:bg-surface-100 transition-smooth"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? "X" : "Menu"}
              size={24}
              color="currentColor"
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute left-0 right-0 top-full bg-background z-40 shadow-xl border-t border-border max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-6 space-y-4">
            {/* Mobile Navigation */}
            <nav className="space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-smooth ${isActivePath(item.path)
                      ? 'bg-primary-50 text-primary border border-primary-200' : 'text-text-secondary hover:text-primary hover:bg-surface-100'
                    }`}
                >
                  <Icon
                    name={item.icon}
                    size={20}
                    color={isActivePath(item.path) ? '#667eea' : 'currentColor'}
                  />
                  <div className="flex flex-col">
                    <span>{item.label}</span>
                    <span className="text-xs text-text-muted font-caption">
                      {item.description}
                    </span>
                  </div>
                </Link>
              ))}
            </nav>

            {/* Mobile Actions */}
            <div className="pt-4 border-t border-border space-y-3">
              {user ? (
                <>
                  <Button
                    variant="ghost"
                    size="md"
                    iconName="Bell"
                    iconSize={20}
                    fullWidth
                    className="justify-start text-text-secondary hover:text-primary"
                    onClick={() => {
                      closeMobileMenu();
                      navigate('/notifications');
                    }}
                  >
                    Notifications
                  </Button>
                  <Button
                    variant="outline"
                    size="md"
                    iconName="User"
                    iconSize={20}
                    fullWidth
                    className="justify-start"
                    onClick={handleDashboard}
                  >
                    Dashboard
                  </Button>
                  <Button
                    variant="ghost"
                    size="md"
                    iconName="LogOut"
                    iconSize={20}
                    fullWidth
                    className="justify-start text-red-600 hover:text-red-700"
                    onClick={handleSignOut}
                    disabled={loading}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="md"
                    iconName="User"
                    iconSize={20}
                    fullWidth
                    className="justify-start"
                    onClick={handleSignIn}
                    disabled={loading}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    iconName="UserPlus"
                    iconSize={20}
                    fullWidth
                    className="justify-start"
                    onClick={handleVendorRegistration}
                  >
                    Join as Vendor
                  </Button>
                </>
              )}
            </div>

            {/* Cultural Context */}
            <div className="pt-4 border-t border-border">
              <div className="px-4 py-3 bg-surface-100 rounded-lg">
                <p className="text-sm text-text-secondary font-caption">
                  Connecting couples with authentic South Asian wedding vendors across the U.S.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;